import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useContent } from "../cms/ContentContext";

function isActiveLink(link) {
  return !(link.active === false || link.active === "false");
}

function sortLinks(links) {
  return [...(links || [])]
    .filter(isActiveLink)
    .sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
}

/**
 * Shared footer — keeps existing markup/classes; links are CMS-driven.
 */
export default function SiteFooter({ onOpenConsultation }) {
  const { content } = useContent();
  const footer = content.footer || {};
  const FOOTER_COLS = footer.columns || [];
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname !== "/") return;
    const hash = (location.hash || window.location.hash || "").replace(/^#/, "");
    if (!hash) return;

    if (hash === "consultation") {
      onOpenConsultation?.();
      return;
    }

    const scrollTarget =
      (hash === "contact" ? document.getElementById("partner") : null) ||
      document.getElementById(hash) ||
      document.getElementById("contact");

    if (scrollTarget) {
      requestAnimationFrame(() => {
        scrollTarget.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [location.pathname, location.hash, onOpenConsultation]);

  const handleLinkClick = (e, link) => {
    const href = (link.href || "").trim();
    const openInNew = link.openInNew === true || link.openInNew === "true";

    if (!href || href === "#") {
      e.preventDefault();
      return;
    }

    if (/^https?:\/\//i.test(href) || href.startsWith("mailto:") || href.startsWith("tel:")) {
      if (openInNew) {
        e.preventDefault();
        window.open(href, "_blank", "noopener,noreferrer");
      }
      return;
    }

    if (href.startsWith("/page/")) {
      e.preventDefault();
      if (openInNew) window.open(href, "_blank", "noopener,noreferrer");
      else navigate(href);
      return;
    }

    if (href.includes("#")) {
      const hash = href.slice(href.indexOf("#") + 1);
      if (!hash) {
        e.preventDefault();
        return;
      }

      if (hash === "consultation") {
        e.preventDefault();
        if (location.pathname !== "/") navigate("/");
        setTimeout(() => onOpenConsultation?.(), 0);
        return;
      }

      e.preventDefault();
      if (location.pathname !== "/") {
        navigate(`/#${hash}`);
        return;
      }

      const scrollTarget =
        (hash === "contact" ? document.getElementById("partner") : null) ||
        document.getElementById(hash) ||
        document.getElementById("contact");
      if (scrollTarget) {
        scrollTarget.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.replaceState(null, "", `/#${hash}`);
      }
      return;
    }

    if (href.startsWith("/")) {
      e.preventDefault();
      navigate(href);
    }
  };

  return (
    <footer className="site-footer">
      <div className="footer-body">
        <div className="footer-inner">
          <div className="footer-brand">
            <span className="footer-logo">{footer.brand}</span>
            <p className="footer-tagline">
              {footer.taglineLine1}
              <br />
              {footer.taglineLine2}
            </p>
            <p className="footer-copy">{footer.copyright}</p>
          </div>

          <div className="footer-cols">
            {FOOTER_COLS.map((col) => (
              <div className="footer-col" key={col.title}>
                <p className="footer-col-title">{col.title}</p>
                <ul>
                  {sortLinks(col.links).map((link) => {
                    const openInNew = link.openInNew === true || link.openInNew === "true";
                    const href = link.href || "#";
                    return (
                      <li key={`${col.title}-${link.label}-${link.order}`}>
                        <a
                          href={href}
                          target={openInNew ? "_blank" : undefined}
                          rel={openInNew ? "noopener noreferrer" : undefined}
                          onClick={(e) => handleLinkClick(e, link)}
                        >
                          {link.label}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
